// 股票列表（Web 版） - 行情 + 搜索 + 过滤 + 交易按钮
import { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Space, Input, Tooltip, App as AntApp } from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';
import { useStore } from '../store/account';
import { api } from '../lib/api';
import type { StockBasic, StockQuote } from '../../shared/types';
import TradeDialog from './TradeDialog';
import StockDetailDrawer from './StockDetailDrawer';

interface PoolRow extends StockBasic {
  quote?: StockQuote;
}

export default function StockList() {
  const { pool, quotes, refreshQuotes, addToPool } = useStore();
  const { message } = AntApp.useApp();

  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<'all' | 'stock' | 'crypto'>('all');
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [tradeOpen, setTradeOpen] = useState(false);
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>('buy');
  const [selectedQuote, setSelectedQuote] = useState<StockQuote | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<PoolRow | null>(null);

  // 搜索补全（防抖调后端）
  const [searchResults, setSearchResults] = useState<StockBasic[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const kw = keyword.trim();
    // 仅在本地 pool 找不到匹配时才走后端搜索
    const localMatch = pool.some(
      (s) => s.code.toLowerCase().includes(kw.toLowerCase()) || s.name.toLowerCase().includes(kw.toLowerCase())
    );
    if (!kw || kw.length < 2 || localMatch) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchStocks(kw);
        const results = r.results || [];
        setSearchResults(results);
        // 搜到结果后，立刻为这些股票刷一次行情（不用等下次 tick）
        // 传完整 StockBasic（含 market/name），后端才能正确路由到东财/Gate.io
        if (results.length > 0) {
          // 1) 把搜索到的股票合并进 pool（去重），让 ticker 后续自动拉行情
          addToPool(results);
          // 2) 立刻拉一次行情刷新
          await refreshQuotes(results);
        }
      } catch (e) {
        // 静默失败（搜索服务不可用不影响主功能）
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [keyword, pool, refreshQuotes, addToPool]);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // 合并本地 pool + 当前搜索结果，让搜索补全的股票也能拿到行情
      const localCodes = new Set(pool.map((s) => s.code));
      const itemsToRefresh: StockBasic[] = [
        ...pool,
        ...searchResults.filter((s) => !localCodes.has(s.code)),
      ];
      await refreshQuotes(itemsToRefresh);
      setLastUpdate(Date.now());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);
 // eslint-disable-line react-hooks/exhaustive-deps

  // 倒计时（下次刷新）
  useEffect(() => {
    if (lastUpdate <= 0) return;
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastUpdate) / 1000);
      const remaining = Math.max(0, 10 - elapsed);
      setCountdown(remaining);
    }, 1000);
    return () => clearInterval(id);
  }, [lastUpdate]);

  const data: PoolRow[] = useMemo(() => {
    const rows: PoolRow[] = pool.map((s) => ({ ...s, quote: quotes[s.code] }));
    let filtered = rows;
    if (filter !== 'all') {
      filtered = filtered.filter((r) => r.type === filter);
    }
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      filtered = filtered.filter(
        (r) => r.code.toLowerCase().includes(kw) || r.name.toLowerCase().includes(kw)
      );

      // 合并后端搜索补全的结果（仅 A 股，且不在本地 pool）
      const localCodes = new Set(rows.map((r) => r.code));
      const extra: PoolRow[] = searchResults
        .filter((s) => !localCodes.has(s.code))
        .filter((s) => filter === 'all' || s.type === filter)
        .map((s) => ({ ...s, quote: quotes[s.code] }));
      filtered = [...filtered, ...extra];
    }
    return filtered;
  }, [pool, quotes, filter, keyword, searchResults]);

  const openTrade = (row: PoolRow, mode: 'buy' | 'sell') => {
    setSelectedQuote(row.quote || null);
    setTradeMode(mode);
    setTradeOpen(true);
  };

  const onTradeClose = (refreshed?: boolean) => {
    setTradeOpen(false);
    if (refreshed) {
      message.success('操作成功');
      handleRefresh();
    }
  };

  const loadedCount = pool.filter((s) => quotes[s.code]?.price > 0).length;

  const columns = [
    {
      title: '代码',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (code: string) => <Tag>{code}</Tag>,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 140,
      render: (name: string, r: PoolRow) => (
        <Space size={4}>
          <span>{name}</span>
          {searchResults.some((s) => s.code === r.code) && (
            <Tooltip title="来自东财搜索，下个 tick 自动加入行情">
              <Tag color="blue">搜索</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '价格',
      dataIndex: 'quote',
      key: 'price',
      width: 120,
      align: 'right' as const,
      render: (q?: StockQuote) =>
        q ? (
          <span style={{ fontWeight: 600 }}>
            ¥{q.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        ) : (
          <span style={{ color: '#8b949e' }}>--</span>
        ),
    },
    {
      title: '涨跌',
      dataIndex: 'quote',
      key: 'change',
      width: 100,
      align: 'right' as const,
      render: (q?: StockQuote) => {
        if (!q) return <span style={{ color: '#8b949e' }}>--</span>;
        const pct = q.changePercent ?? 0;
        const color = pct > 0 ? '#ff4d4f' : pct < 0 ? '#52c41a' : '#8b949e';
        const Icon = pct > 0 ? RiseOutlined : pct < 0 ? FallOutlined : null;
        return (
          <span style={{ color, fontWeight: 600 }}>
            {Icon ? <Icon /> : null} {pct.toFixed(2)}%
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_: any, r: PoolRow) => {
        const disabled = !r.quote;
        return (
          <Space>
            <Button
              size="small"
              onClick={() => {
                setSelectedStock(r);
                setDetailOpen(true);
              }}
              title="K线 + 合约交易"
            >
              详情
            </Button>
            <Tooltip title={!r.quote ? '暂无行情' : ''}>
              <Button
                size="small"
                type="primary"
                danger
                disabled={disabled}
                onClick={() => openTrade(r, 'buy')}
              >
                买入
              </Button>
            </Tooltip>
            <Button size="small" disabled={disabled} onClick={() => openTrade(r, 'sell')}>
              卖出
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="hg-panel">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Space size="middle">
          <span style={{ fontSize: 14, fontWeight: 600 }}>📈 行情</span>
          <Tag color={loadedCount > 0 ? 'success' : 'warning'}>
            {loadedCount}/{pool.length} 已加载
          </Tag>
          {lastUpdate > 0 && (
            <span style={{ color: '#8b949e', fontSize: 12 }}>下次刷新 {countdown}s</span>
          )}
        </Space>
        <Space>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索代码/名称"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: 180 }}
          />
          <Button.Group>
            <Button
              type={filter === 'all' ? 'primary' : 'default'}
              size="small"
              onClick={() => setFilter('all')}
            >
              全部
            </Button>
            <Button
              type={filter === 'stock' ? 'primary' : 'default'}
              size="small"
              onClick={() => setFilter('stock')}
            >
              A 股
            </Button>
            <Button
              type={filter === 'crypto' ? 'primary' : 'default'}
              size="small"
              onClick={() => setFilter('crypto')}
            >
              加密货币
            </Button>
          </Button.Group>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(255, 77, 79, 0.1)',
            border: '1px solid #ff4d4f',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8,
            color: '#ff4d4f',
            fontSize: 12,
          }}
        >
          ⚠️ 行情拉取失败: {error}（已加入自动重试）
        </div>
      )}

      <Table
        rowKey="code"
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ x: 760 }}
        pagination={false}
        style={{ background: 'transparent' }}
        locale={{
          emptyText: keyword
            ? searching
              ? `正在搜索 "${keyword}"...`
              : `没找到 "${keyword}"`
            : '暂无行情数据',
        }}
      />

      <TradeDialog
        open={tradeOpen}
        mode={tradeMode}
        quote={selectedQuote}
        onClose={onTradeClose}
      />
      <StockDetailDrawer
        open={detailOpen}
        stock={selectedStock}
        quote={selectedQuote}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}